import { Alert } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { SegmentPreview } from '@/pages/SegmentBuilderPage'

export function SegmentPreviewPanel({
  preview,
  loading,
  error,
}: {
  preview?: SegmentPreview
  loading: boolean
  error: boolean
}) {
  return (
    <Card className="w-full max-w-4xl">
      <CardContent className="p-0">
        {loading ? (
          <p className="px-4 py-3 text-body-sm text-muted-foreground">Updating preview…</p>
        ) : error || !preview ? (
          <Alert className="m-4">Segment preview unavailable</Alert>
        ) : (
          <>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-4 py-3">
              <span className="text-body-sm font-semibold">
                {matchCountLabel(preview.match_count)}
              </span>
              <span className="text-caption text-muted-foreground">
                Excluded · {exclusionSummary(preview.exclusions)}
              </span>
            </div>
            <Table className="border-t border-border">
              <TableHeader>
                <TableRow>
                  <TableHead className="h-8 pl-4">Sample</TableHead>
                  <TableHead className="h-8">Email</TableHead>
                  <TableHead className="h-8">Level</TableHead>
                  <TableHead className="h-8 pr-4 text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.sample.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="py-6 text-center text-body-sm text-muted-foreground"
                    >
                      {preview.match_count === 0
                        ? 'No Contacts match this rule.'
                        : 'No sample contacts available.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  preview.sample.map((match) => (
                    <TableRow key={match.id}>
                      <TableCell className="py-2 pl-4 text-body-sm font-medium">
                        {match.name}
                      </TableCell>
                      <TableCell className="py-2 text-body-sm text-muted-foreground">
                        {match.email}
                      </TableCell>
                      <TableCell className="py-2 text-body-sm text-muted-foreground">
                        {match.membership_level}
                      </TableCell>
                      <TableCell className="py-2 pr-4 text-right text-body-sm">
                        {match.membership_status}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {preview.excluded.length > 0 && (
              <div className="border-t border-border px-4 py-3">
                <p className="pb-1 text-caption font-medium text-muted-foreground">
                  Excluded Contacts
                </p>
                <p className="text-caption text-muted-foreground">
                  {preview.excluded
                    .map((item) => `${item.name}: ${item.reason.replaceAll('_', ' ')}`)
                    .join(' · ')}
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function matchCountLabel(count: number) {
  return `${count} ${count === 1 ? 'match' : 'matches'}`
}

function exclusionSummary(exclusions: SegmentPreview['exclusions']) {
  return (
    [
      ['opted_out', 'opted out'],
      ['suppressed', 'suppressed'],
      ['no_email_address', 'no email address'],
      ['archived', 'archived'],
    ] as const
  )
    .map(([key, label]) => `${exclusions[key] ?? 0} ${label}`)
    .join(' · ')
}
